import {
  CalendarPlus, Check, ChevronDown, ClipboardList, ExternalLink, Megaphone, NotebookPen, Plus, Star, UserPlus, X,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Avatar, Switch, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip } from "@/components/ui/misc";
import { NewAppointmentDialog } from "@/features/appointment-new";
import { IntakeReadback } from "@/features/intake-readback/panel";
import { ContactDetailsPanel } from "@/features/record-details";
import {
  conversationTitle, DND_CHANNELS, dndOn, type ContactField, type Conversation, type DndKey, type Person,
} from "@/lib/conversations";
import type { RecordSection } from "@/lib/types";
import { cn, formatPhoneNumber } from "@/lib/utils";
import { seesCampaigns } from "@/lib/types";
import { useAuth } from "@/lib/auth";

const NOT_CONNECTED = "Not connected yet";

/**
 * Who the contact is, beside the conversation: owner, followers and tags at the top, then the
 * tabs — every field in its folder (searchable), the intake as it was asked and answered
 * (ACL-477), do-not-disturb per channel, and the actions a rep takes on a contact.
 *
 * **What is editable here.** The All fields tab is the contact record's details panel, as the contact
 * page draws it (ACL-455): each field changes where it is read, the form's own through
 * `PATCH /leads/{id}/fields` and the contact's own details (name, phone, e-mail...) through the
 * contact, as far as the reader's role may (`detailEditable`); the server decides which a role sees
 * and may change. Owner, followers, tags and DND change the page's state for now.
 * `organize` off (core-api's conversations, which keep none of them yet: `ORGANIZING_KEPT`) leaves
 * owner, followers, tags and DND out.
 */
export function ContactPanel({
  conversation: c, members, onClose, onTag, onOwner, onFollower, onDnd, organize = true, className,
}: {
  conversation: Conversation;
  /** Who can own or follow the thread. */
  members: Person[];
  onClose: () => void;
  onTag: (tag: string, on: boolean) => void;
  onOwner: (owner: Person | null) => void;
  onFollower: (person: Person, on: boolean) => void;
  onDnd: (key: DndKey, on: boolean) => void;
  organize?: boolean;
  className?: string;
}) {
  const title = conversationTitle(c);
  return (
    <aside
      className={cn("flex w-full min-w-0 flex-col border-border bg-surface xl:w-80 xl:shrink-0 xl:border-l", className)}
      aria-label="Contact details"
    >
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
        <h2 className="text-sm font-semibold text-text">Contact Details</h2>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Close contact details">
          <X />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle">
        <div className="space-y-4 border-b border-border p-3">
          <div className="flex items-center gap-2.5">
            <Avatar name={c.contactName} className="size-9" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-text">{title}</p>
              <p className="truncate text-xs text-text-muted">
                {[c.phone && formatPhoneNumber(c.phone), c.email].filter(Boolean).join(" · ")}
              </p>
            </div>
            {c.leadId ? (
              <Tooltip content="Open contact record">
                <Button variant="ghost" size="icon-sm" asChild>
                  <Link to={`/contacts/${c.leadId}`} aria-label="Open contact record">
                    <ExternalLink />
                  </Link>
                </Button>
              </Tooltip>
            ) : (
              <Tooltip content="Not matched to a contact record">
                <span tabIndex={0}>
                  <Button variant="ghost" size="icon-sm" disabled aria-label="Open contact record">
                    <ExternalLink />
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>

          {organize && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <OwnerPicker owner={c.contact.owner} members={members} onOwner={onOwner} />
                <FollowerPicker followers={c.followers} members={members} onFollower={onFollower} />
              </div>

              <Tags tags={c.contact.tags} onTag={onTag} />
            </>
          )}
        </div>

        <Tabs defaultValue="fields">
          <TabsList className="px-3 pt-1">
            <TabsTrigger value="fields">All fields</TabsTrigger>
            <TabsTrigger value="intake">Intake</TabsTrigger>
            {organize && <TabsTrigger value="dnd">DND</TabsTrigger>}
            <TabsTrigger value="actions">Actions</TabsTrigger>
          </TabsList>
          <TabsContent value="fields" className="p-3">
            <Fields conversation={c} />
          </TabsContent>
          {/* The questions as they were asked, which the All fields tab cannot show: it holds the
              same answers as loose values, with no question, no order and no sign of a skip. */}
          <TabsContent value="intake" className="p-3">
            <IntakeReadback leadId={c.leadId} />
          </TabsContent>
          {organize && (
            <TabsContent value="dnd" className="p-3">
              <Dnd conversation={c} onDnd={onDnd} />
            </TabsContent>
          )}
          <TabsContent value="actions" className="p-3">
            <Actions conversation={c} />
          </TabsContent>
        </Tabs>
      </div>
    </aside>
  );
}

function OwnerPicker({
  owner, members, onOwner,
}: {
  owner: Person | null;
  members: Person[];
  onOwner: (owner: Person | null) => void;
}) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-2xs font-medium text-text-muted">Owner</p>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Owner: ${owner?.name ?? "Unassigned"}. Change owner`}
            className="flex h-7 w-full min-w-0 items-center gap-1.5 rounded-md border border-border bg-surface px-2 text-xs text-text hover:bg-surface-hover"
          >
            {owner ? (
              <>
                <Avatar name={owner.name} className="size-4 text-[8px]" />
                <span className="truncate">{owner.name}</span>
              </>
            ) : (
              <span className="text-text-subtle">Unassigned</span>
            )}
            <ChevronDown className="ml-auto size-3 shrink-0 text-text-subtle" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuLabel>Owner</DropdownMenuLabel>
          {members.map((m) => (
            <DropdownMenuItem key={m.id} onSelect={() => onOwner(m)}>
              <Check className={cn(owner?.id !== m.id && "invisible")} />
              {m.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => onOwner(null)}>
            <Check className={cn(owner !== null && "invisible")} />
            Unassigned
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function FollowerPicker({
  followers, members, onFollower,
}: {
  followers: Person[];
  members: Person[];
  onFollower: (person: Person, on: boolean) => void;
}) {
  const following = new Set(followers.map((p) => p.id));
  return (
    <div className="min-w-0">
      <p className="mb-1 text-2xs font-medium text-text-muted">Followers ({followers.length})</p>
      <div className="flex h-7 items-center gap-1">
        <div className="flex -space-x-1.5">
          {followers.slice(0, 4).map((p) => (
            <Tooltip key={p.id} content={p.name}>
              <span>
                <Avatar name={p.name} className="size-6 ring-2 ring-surface" />
              </span>
            </Tooltip>
          ))}
        </div>
        {followers.length > 4 && <span className="text-2xs text-text-muted">+{followers.length - 4}</span>}
        {followers.length === 0 && <span className="text-xs text-text-subtle">None</span>}
        <DropdownMenu>
          <Tooltip content="Add or remove followers">
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Add or remove followers">
                <UserPlus />
              </Button>
            </DropdownMenuTrigger>
          </Tooltip>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Followers</DropdownMenuLabel>
            {members.map((m) => (
              <DropdownMenuCheckboxItem
                key={m.id}
                checked={following.has(m.id)}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={(on) => onFollower(m, on === true)}
              >
                {m.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

function Tags({ tags, onTag }: { tags: string[]; onTag: (tag: string, on: boolean) => void }) {
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const add = () => {
    if (draft.trim()) onTag(draft, true);
    setDraft("");
    setAdding(false);
  };
  return (
    <div>
      <div className="mb-1 flex items-center gap-1">
        <p className="text-2xs font-medium text-text-muted">Tags ({tags.length})</p>
        <Button variant="ghost" size="icon-sm" className="size-5" aria-label="Add tag" onClick={() => setAdding(true)}>
          <Plus />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => (
          <Badge key={t} tone="outline" className="gap-0.5 pr-1">
            {t}
            <button
              type="button"
              onClick={() => onTag(t, false)}
              className="rounded p-0.5 text-text-subtle hover:bg-surface-hover hover:text-text"
              aria-label={`Remove tag ${t}`}
            >
              <X className="size-3" aria-hidden />
            </button>
          </Badge>
        ))}
        {tags.length === 0 && !adding && <span className="text-xs text-text-subtle">No tags</span>}
        {adding && (
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={add}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
              if (e.key === "Escape") {
                setDraft("");
                setAdding(false);
              }
            }}
            placeholder="New tag"
            aria-label="New tag"
            className="h-6 w-28 text-xs"
          />
        )}
      </div>
    </div>
  );
}

/**
 * Every field in its folder, searchable (ACL-392): the contact record's own details panel (ACL-455),
 * the same one the contact page draws, so the two read and change a field alike. Until the record's
 * fields have come with something this role may see (while they load, on a 404 or an error, with every
 * folder hidden, or for a sender with no record), it shows the thread's Contact folder, read-only.
 */
function Fields({ conversation: c }: { conversation: Conversation }) {
  return (
    // Keyed by the thread, so the search and the open folders start afresh for each.
    <ContactDetailsPanel
      key={c.id}
      contact={c.leadId ? { id: c.leadId } : null}
      compact
      fallback={[threadSection(c.contact.details)]}
    />
  );
}

/** The thread's details as a record's folder, so the panel draws them as it draws the record's. */
function threadSection(details: readonly ContactField[]): RecordSection {
  return {
    key: "contact",
    title: "Contact",
    sort_order: 0,
    fields: details.map((f, i) => ({
      key: f.key,
      title: f.label,
      type: f.type ?? "text",
      required: false,
      sort_order: i,
      system: false,
      storage: "value",
      config: {},
      value: f.value,
      access: "read",
      can_edit: false,
    })),
  };
}

function Dnd({ conversation: c, onDnd }: { conversation: Conversation; onDnd: (key: DndKey, on: boolean) => void }) {
  const all = c.contact.dnd.all;
  return (
    <div className="space-y-3">
      <p className="text-xs text-text-muted">
        Do not disturb stops the team reaching this contact on a channel. All channels covers every
        one of them; turn it off to set them one by one.
      </p>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {DND_CHANNELS.map((ch) => {
          const on = ch.key === "all" ? all : dndOn(c.contact.dnd, ch.key);
          // Under All channels a single channel cannot be turned back on: All has to go first.
          const locked = ch.key !== "all" && all;
          return (
            <li key={ch.key} className="flex items-center justify-between px-3 py-2">
              <span className={cn("text-sm", ch.key === "all" ? "font-medium text-text" : "text-text")}>{ch.label}</span>
              <span className="flex items-center gap-2">
                <span className={cn("text-2xs", on ? "text-danger-11" : "text-text-subtle")}>
                  {locked ? "On (All channels)" : on ? "DND on" : "Off"}
                </span>
                <Switch
                  checked={on}
                  disabled={locked}
                  onCheckedChange={(next) => onDnd(ch.key, next)}
                  aria-label={`Do not disturb: ${ch.label}`}
                />
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Why a thread's contact cannot be booked: a sender who is no contact, or one the reader may not open. */
const NO_RECORD = "Only a contact whose record you can open can be booked";

function Actions({ conversation: c }: { conversation: Conversation }) {
  const [booking, setBooking] = React.useState(false);
  const sees = seesCampaigns(useAuth().user?.role);
  const actions = [
    ...(sees ? [{ icon: Megaphone, label: "Add to campaign" }] : []),
    { icon: CalendarPlus, label: "Book appointment" },
    { icon: ClipboardList, label: "Add task" },
    { icon: Star, label: "Send review request" },
  ];
  return (
    <div className="space-y-1.5">
      {c.leadId && (
        <Button variant="outline" className="w-full justify-start" asChild>
          <Link to={`/contacts/${c.leadId}?compose=1`}>
            <NotebookPen />
            Add a note on the contact
          </Link>
        </Button>
      )}
      {actions.map((a) =>
        a.label === "Book appointment" && c.leadId ? (
          <Button key={a.label} variant="outline" className="w-full justify-start" onClick={() => setBooking(true)}>
            <a.icon />
            {a.label}
          </Button>
        ) : (
          <Tooltip key={a.label} content={a.label === "Book appointment" ? NO_RECORD : NOT_CONNECTED}>
            <span tabIndex={0} className="block">
              <Button variant="outline" className="w-full justify-start" disabled>
                <a.icon />
                {a.label}
              </Button>
            </span>
          </Tooltip>
        ),
      )}
      {c.leadId && (
        <NewAppointmentDialog
          open={booking}
          onOpenChange={setBooking}
          contact={{ id: c.leadId, name: conversationTitle(c), phone: c.phone }}
          source="conversation"
        />
      )}
    </div>
  );
}
